import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ROUTES } from '../../../constants/routes';
import { getSessionVar, setSessionVar, sortOrganizationsAndProjects } from '../../../lib/helpers';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { rivermeadowApi, useChangeOrganizationMutation, useGetOrganizationQuery } from '../../../api';

export const useOrganization = ({ setOpenKeys }) => {
    const dispatch = useDispatch();
    const { notifyError } = useNotificationContext();
    const [organizations, setOrganizations] = useState([]);
    const [changeOrgMutation] = useChangeOrganizationMutation();
    const [currentOrganizationName, setCurrentOrganizationName] = useState('Default Organization Name');
    const [currentOrganizationId, setCurrentOrganizationId] = useState(sessionStorage.getItem('userOrg'));

    const curOrgID = sessionStorage.getItem('userOrg');
    const {
        data: organizationData,
        isSuccess,
        isError,
        error,
    } = useGetOrganizationQuery({ orgId: curOrgID }, { skip: !curOrgID || !!getSessionVar('currentOrg') });

    useEffect(() => {
        const orgsFromSession = getSessionVar('organizations') || [];
        setOrganizations(sortOrganizationsAndProjects(orgsFromSession));
        const curOrgID = sessionStorage.getItem('userOrg');
        setCurrentOrganizationId(curOrgID);
        if (curOrgID) {
            const currentOrg = orgsFromSession.find((org) => org.id === curOrgID);
            if (currentOrg) {
                let orgName = currentOrg.name;
                if (currentOrg.is_project) {
                    const parentOrg = orgsFromSession.find((org) => org.id === currentOrg.parent_organization_id);
                    if (parentOrg) {
                        orgName = `${parentOrg.name}: ${orgName}`;
                    }
                }
                setCurrentOrganizationName(orgName);
            }
        }
    }, []);

    useEffect(() => {
        const handleOrganizationsUpdated = () => {
            const orgsFromSession = getSessionVar('organizations') || [];
            setOrganizations(sortOrganizationsAndProjects(orgsFromSession));
        };
        // Listen for organization updates from admin_projects_module.js
        window.addEventListener('organizationsUpdated', handleOrganizationsUpdated);
        return () => window.removeEventListener('organizationsUpdated', handleOrganizationsUpdated);
    }, []);

    // Handle successful organization query
    useEffect(() => {
        if (isSuccess && organizationData) {
            setSessionVar('currentOrg', organizationData);
        }
    }, [isSuccess, organizationData]);

    // Handle organization query errors
    useEffect(() => {
        if (isError) {
            console.error('Failed to fetch organization:', error);
            notifyError({ description: 'Failed to fetch organization' });
        }
    }, [isError, error, notifyError]);

    const changeOrganization = async (orgId) => {
        try {
            const userId = sessionStorage.getItem('loggedInUserId');
            if (!userId) {
                return;
            }
            await changeOrgMutation({ orgId, userId });
            sessionStorage.setItem('userOrg', orgId);
            const user = getSessionVar('user');
            user.organization = orgId;
            setSessionVar('user', user);
            sessionStorage.removeItem('userProjectRole');
            sessionStorage.removeItem('currentOrg');
            localStorage.removeItem(userId + '_menuCollapsed');
            setOpenKeys([]);
            dispatch(rivermeadowApi.util.resetApiState());
            window.reactNavigate(ROUTES.home, true);
        } catch (error) {
            notifyError({ description: 'Failed to change organization' });
        }
    };

    return {
        organizations,
        currentOrganizationId,
        currentOrganizationName,
        changeOrganization,
    };
};
